"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TrendingUp, Search, MapPin, ChartBar as BarChart3, Target, ArrowUpRight, ShieldCheck, Zap, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const warringtonKeywords = [{
  id: 1,
  keyword: "SEO Consultant Warrington",
  volume: "880/mo",
  intent: "High Commercial",
  opportunity: "High",
  competition: "Medium"
}, {
  id: 2,
  keyword: "Local SEO Warrington",
  volume: "590/mo",
  intent: "Transactional",
  opportunity: "Very High",
  competition: "Low-Medium"
}, {
  id: 3,
  keyword: "SEO Agency Cheshire & Warrington",
  volume: "1,200/mo",
  intent: "Commercial",
  opportunity: "High",
  competition: "High"
}, {
  id: 4,
  keyword: "B2B Lead Generation Warrington",
  volume: "450/mo",
  intent: "High Intent",
  opportunity: "Very High",
  competition: "Low"
}];
const competitorGaps = [{
  id: 1,
  title: "Generic Agency Templates",
  desc: "Most regional agencies deploy mass automated audits that miss technical indexing issues unique to Cheshire businesses."
}, {
  id: 2,
  title: "Neglected Google Business Profiles",
  desc: "Over 60% of local service businesses in Warrington lack optimized local schema and entity geo-relevance."
}, {
  id: 3,
  title: "Unclaimed High-Intent B2B Keywords",
  desc: "Local competitors focus strictly on generic terms, leaving intent-rich commercial queries wide open for fast rankings."
}];
const opportunityHighlights = [{
  id: 1,
  stat: "+140%",
  label: "Regional Search Growth",
  detail: "Year-on-year increase in Warrington and Cheshire businesses searching for direct independent SEO expertise."
}, {
  id: 2,
  stat: "Top 3",
  label: "Local Map Pack Placement",
  detail: "My targeted local entity optimization pushes Warrington businesses directly into top Google local results."
}, {
  id: 3,
  stat: "100%",
  label: "Direct Consultant Access",
  detail: "You work directly with me (Yassine Essaadi), ensuring senior-level SEO execution without middleman overhead."
}];
const neighboringCities = [{
  id: 1,
  name: "Stoke on Trent",
  path: "/LocalSeoConsultantStokeOnTrentPage",
  distance: "32 miles",
  region: "Staffordshire"
}, {
  id: 2,
  name: "Derby",
  path: "/LocalSeoConsultantDerbyPage",
  distance: "58 miles",
  region: "East Midlands"
}, {
  id: 3,
  name: "Wakefield",
  path: "/LocalSeoConsultantWakefieldPage",
  distance: "54 miles",
  region: "West Yorkshire"
}, {
  id: 4,
  name: "Milton Keynes",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  distance: "135 miles",
  region: "Buckinghamshire"
}, {
  id: 5,
  name: "Northampton",
  path: "/LocalSeoConsultantNorthamptonPage",
  distance: "128 miles",
  region: "Northamptonshire"
}];
export default function Warrington() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".gsap-reveal", {
        y: 30
      });
      gsap.to(".gsap-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".gsap-reveal", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="warringtonmarketanalysis" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div ref={containerRef} className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Heading */}
        <div className="gsap-reveal max-w-3xl mb-12" style={{
        opacity: 0
      }}>
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs gap-1.5">
              <MapPin className="size-3.5 text-primary" />
              <span>Warrington &amp; Cheshire Market Analysis</span>
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground font-default mb-4">
            Warrington Search Trends &amp; Local SEO Opportunities
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            I conduct thorough regional market breakdowns to identify untapped search volume in Warrington.
            When you work directly with me, we target high-converting local intent rather than generic high-competition keywords.
          </p>
        </div>

        {/* Opportunity Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {opportunityHighlights.map((item, i) => <div key={item.id} data-index={i} className="gsap-reveal" style={{
          opacity: 0
        }}>
              <Card className="border-border bg-card shadow-xs h-full flex flex-col justify-between">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {item.label}
                    </span>
                    <TrendingUp className="size-4 text-primary" />
                  </div>
                  <CardTitle className="text-3xl font-bold text-primary font-default">
                    {item.stat}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.detail}
                  </p>
                </CardContent>
              </Card>
            </div>)}
        </div>

        {/* Local Keywords Table */}
        <div className="gsap-reveal mb-12" style={{
        opacity: 0
      }}>
          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="border-b border-border pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-semibold text-foreground font-default flex items-center gap-2">
                    <Search className="size-5 text-primary" />
                    <span>Warrington Keyword Density &amp; Intent</span>
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground mt-1">
                    Representative search queries driving organic commercial traffic across Cheshire.
                  </CardDescription>
                </div>
                <Badge variant="outline" className="w-fit text-xs border-primary/30 text-primary">
                  Updated Quarter Analysis
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="font-semibold text-foreground">Target Keyword</TableHead>
                    <TableHead className="font-semibold text-foreground">Est. Volume</TableHead>
                    <TableHead className="font-semibold text-foreground">Search Intent</TableHead>
                    <TableHead className="font-semibold text-foreground">Opportunity Level</TableHead>
                    <TableHead className="font-semibold text-foreground">Competition</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {warringtonKeywords.map((kw, i) => <TableRow key={kw.id} data-index={i} className="border-border">
                      <TableCell className="font-medium text-foreground py-3.5">
                        {kw.keyword}
                      </TableCell>
                      <TableCell className="text-muted-foreground py-3.5">
                        {kw.volume}
                      </TableCell>
                      <TableCell className="py-3.5">
                        <Badge variant="secondary" className="text-xs">
                          {kw.intent}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3.5 text-foreground font-medium">
                        <span className="inline-flex items-center gap-1">
                          <Zap className="size-3.5 text-accent" />
                          {kw.opportunity}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground py-3.5">
                        {kw.competition}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Competitor Gap + Direct Consultant Value */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Competitor Gaps */}
          <div className="gsap-reveal" style={{
          opacity: 0
        }}>
            <Card className="border-border bg-card shadow-xs h-full">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-foreground font-default flex items-center gap-2">
                  <BarChart3 className="size-5 text-primary" />
                  <span>Regional Competitor Analysis</span>
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Common weaknesses found in existing Warrington SEO market strategies.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {competitorGaps.map((gap, i) => <div key={gap.id} data-index={i} className="flex gap-3 items-start pb-3 border-b border-border last:border-0 last:pb-0">
                    <div className="mt-0.5 p-1 rounded-md bg-muted text-primary shrink-0">
                      <Target className="size-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground font-default">
                        {gap.title}
                      </h4>
                      <p className="text-xs md:text-sm text-muted-foreground mt-0.5 leading-relaxed">
                        {gap.desc}
                      </p>
                    </div>
                  </div>)}
              </CardContent>
            </Card>
          </div>

          {/* Direct Value & CTA */}
          <div className="gsap-reveal" style={{
          opacity: 0
        }}>
            <Card className="border-border bg-card shadow-xs h-full flex flex-col justify-between">
              <div>
                <CardHeader>
                  <CardTitle className="text-xl font-semibold text-foreground font-default flex items-center gap-2">
                    <ShieldCheck className="size-5 text-primary" />
                    <span>Why Work Directly With Me</span>
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    A tailored consultancy model designed for sustainable organic search expansion.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" />
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Zero agency bloat:</strong> Every deliverable, audit, and strategy is authored directly by me (Yassine Essaadi).
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" />
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Technical precision:</strong> Deep cleanups of crawl errors, schema markup, and speed performance.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" />
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Transparent measurement:</strong> Clear revenue-focused reporting without inflated vanity metrics.
                    </p>
                  </div>
                </CardContent>
              </div>
              <div className="p-6 pt-0">
                <Button asChild size="lg" className="w-full">
                  <Link to="/ContactPage">
                    <span>Book a Free SEO Consultation</span>
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </div>

        <Separator className="my-10" />

        {/* Nearby Regional Landing Pages */}
        <div className="gsap-reveal" style={{
        opacity: 0
      }}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground font-default">
                Nearby Regional SEO Consulting Pages
              </h3>
              <p className="text-sm text-muted-foreground">
                Explore tailored local SEO insights and strategies across neighboring business centers.
              </p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/LocalSEOPage">
                <span>All Local SEO Locations</span>
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {neighboringCities.map((city, i) => <div key={city.id} data-index={i}>
                <Link to={city.path} className="group block p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors h-full">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="secondary" className="text-[11px] px-2 py-0">
                      {city.distance}
                    </Badge>
                    <ArrowUpRight className="size-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm font-default">
                    {city.name}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {city.region}
                  </div>
                </Link>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}